import React, { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import { Link, useNavigate } from '@tanstack/react-router';
import { type Area } from 'react-easy-crop';
import { DateInput } from '../ui/DateInput';
import { FormLabel } from '../ui/FormLabel';
import {
  FiSave,
  FiCamera,
  FiTrash2,
  FiUser,
  FiAtSign,
  FiMail,
  FiPhone,
  FiMapPin,
  FiGlobe,
  FiCalendar,
  FiSearch,
  FiPlus,
  FiX,
  FiAlertCircle,
} from 'react-icons/fi';
import {
  SiSpotify,
  SiApplemusic,
  SiSoundcloud,
  SiYoutube,
  SiInstagram,
  SiTiktok,
} from 'react-icons/si';
import { FaXTwitter, FaAmazon } from 'react-icons/fa6';
import { useAuth } from '../../hooks/useAuth';
import {
  useEditProfileMutation,
  useUsernameAvailability,
  type EditProfilePayload,
} from '../../api/profile';
import { useCreateGenreMutation, useGetGenresQuery } from '../../api/genres';
import { useGetUserTypes } from '../../api/settings';
import { normalizeGenreKey, toSlugCandidate, buildGenreSlugMap } from '../../utils/genre';
import { useQueryClient } from '@tanstack/react-query';
import { Header } from '../layout/Header';
import { BottomNav } from '../layout/BottomNav';
import { toast } from '../../utils/toast';
import { LocationSearchInput } from '../common/LocationSearchInput';
import { type LocationBody } from '../../services/locationService';
import { useUnsavedChangesGuard } from '../../hooks/useUnsavedChangesGuard';
import { UnsavedChangesModal } from '../common/UnsavedChangesModal';
import { CoverCropModal } from '../common/CoverCropModal';
import { uploadFilesToCloudinary } from '../../utils/cloudinary';
import {
  createCroppedImageFile,
  convertHeicToJpeg,
  getImageDimensions,
  isWholeImageCrop,
} from '../../utils/imageCrop';
import { Avatar, CoverImage } from '../ui/Avatar';

// ─── Input Field Components ───────────────────────────────────────────────────

function FormSection({
  title,
  accentColor = '#3b82f6',
  children,
}: {
  title: string;
  accentColor?: string;
  children: React.ReactNode;
}) {
  return (
    <div className="flex flex-col gap-4">
      <div className="flex items-center gap-2">
        <span className="w-1 h-5 rounded-full" style={{ background: accentColor }} />
        <h2 className="text-white font-bold text-[16px]">{title}</h2>
      </div>
      {children}
    </div>
  );
}

function FormInput({
  id,
  label,
  optional = false,
  icon: Icon,
  iconColor,
  type = 'text',
  placeholder,
  value,
  onChange,
  disabled,
  status,
  statusMsg,
}: {
  id: string;
  label: string;
  optional?: boolean;
  icon?: React.ElementType;
  iconColor?: string;
  type?: string;
  placeholder?: string;
  value: string;
  onChange: (v: string) => void;
  disabled?: boolean;
  status?: 'idle' | 'checking' | 'available' | 'taken' | 'error';
  statusMsg?: string;
}) {
  const statusColors: Record<string, string> = {
    idle: 'border-white/15',
    checking: 'border-blue-500/30',
    available: 'border-green-500/40',
    taken: 'border-red-500/40',
    error: 'border-red-500/40',
  };

  return (
    <div className="flex flex-col gap-1.5">
      <FormLabel
        htmlFor={id}
        optional={optional}
        className="text-zinc-400 text-[13px] font-medium"
        labelClassName="text-zinc-400"
      >
        {label}
      </FormLabel>
      <div
        className={`flex items-center gap-3 px-4 py-3 rounded-xl border transition-all duration-300 ${
          disabled
            ? 'bg-zinc-900/40 border-white/10 text-zinc-600'
            : `bg-zinc-900/60 ${statusColors[status || 'idle']} focus-within:border-blue-500/50`
        }`}
      >
        {Icon && (
          <Icon
            size={15}
            style={{
              color:
                status === 'taken' || status === 'error'
                  ? '#f87171'
                  : status === 'available'
                    ? '#4ade80'
                    : iconColor || '#52525b',
            }}
            className="shrink-0 transition-colors duration-300"
          />
        )}
        <input
          id={id}
          type={type}
          placeholder={placeholder}
          value={value}
          disabled={disabled}
          onChange={(e) => onChange(e.target.value)}
          className={`flex-1 bg-transparent border-none text-white text-[14px] placeholder:text-zinc-600 focus:outline-none focus:ring-0 disabled:cursor-not-allowed ${type === 'date' ? 'scheme-dark' : ''}`}
        />
        {status === 'checking' && (
          <div className="w-4 h-4 border-2 border-white/20 border-t-white rounded-full animate-spin shrink-0" />
        )}
      </div>
      {statusMsg && (
        <p
          className={`text-[11px] font-medium ml-1 mt-0.5 transition-all ${
            status === 'available' ? 'text-green-400' : 'text-red-400'
          }`}
        >
          {statusMsg}
        </p>
      )}
    </div>
  );
}

// ─── Main Page ────────────────────────────────────────────────────────────────

export const EditProfilePage: React.FC = () => {
  const { user, updateUser } = useAuth();
  const asOptionalString = (value: unknown): string | undefined =>
    typeof value === 'string' ? value : undefined;
  const asOptionalNullableString = (value: unknown): string | null | undefined => {
    if (value === null) return null;
    return typeof value === 'string' ? value : undefined;
  };
  const getUserTypesFromUser = (userData: unknown): string[] => {
    if (!userData || typeof userData !== 'object') return [];
    const source = userData as {
      userType?: unknown;
      user_type?: unknown;
      userTypes?: unknown;
      user_types?: unknown;
    };
    const rawUserTypes = source.userTypes ?? source.user_types;
    if (Array.isArray(rawUserTypes)) {
      return rawUserTypes
        .map((item) => {
          if (typeof item === 'string') return item.trim();
          if (item && typeof item === 'object' && 'name' in item) {
            const name = (item as { name?: unknown }).name;
            return typeof name === 'string' ? name.trim() : '';
          }
          return '';
        })
        .filter(Boolean);
    }
    if (typeof source.userType === 'string' && source.userType.trim())
      return [source.userType.trim()];
    if (typeof source.user_type === 'string' && source.user_type.trim())
      return [source.user_type.trim()];
    return [];
  };

  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { mutateAsync: editProfile, isPending: isUpdatingProfile } = useEditProfileMutation();
  const [isUploadingImages, setIsUploadingImages] = useState(false);
  const isPending = isUpdatingProfile || isUploadingImages;

  const { data: userTypesData = [] } = useGetUserTypes();

  // ── Form state ────────────────────────────────────────────────────────────
  const [fullName, setFullName] = useState(user?.fullName || '');
  const [username, setUsername] = useState(user?.username || '');
  const [bio, setBio] = useState(user?.bio || '');
  const [dob, setDob] = useState(user?.dateOfBirth || '');
  const [email, setEmail] = useState(user?.email || '');
  const [phone, setPhone] = useState(user?.phoneNumber || '');

  // Structured Location State
  const [city, setCity] = useState(user?.city || '');
  const [state, setState] = useState(user?.state || '');
  const [country, setCountry] = useState(user?.country || '');

  // Music platforms
  const [spotify, setSpotify] = useState(user?.spotify || '');
  const [appleMusic, setAppleMusic] = useState(user?.appleMusic || '');
  const [soundcloud, setSoundcloud] = useState(user?.soundcloud || '');
  const [amazonMusic, setAmazonMusic] = useState(user?.amazonMusic || '');
  const [youtubeMusic, setYoutubeMusic] = useState(user?.youtubeMusic || '');

  // Social platforms
  const [instagram, setInstagram] = useState(user?.instagram || '');
  const [twitter, setTwitter] = useState(user?.twitter || '');
  const [tiktok, setTiktok] = useState(user?.tiktok || '');
  const [website, setWebsite] = useState(user?.website || '');

  // Role and Genres
  const [userTypesSelected, setUserTypesSelected] = useState<string[]>(getUserTypesFromUser(user));
  const [genres, setGenres] = useState<string[]>(user?.genres || []);
  const [genreInput, setGenreInput] = useState('');
  const [customGenres, setCustomGenres] = useState<string[]>([]);
  const { data: genresData, isLoading: isLoadingGenres } = useGetGenresQuery();
  const createGenreMutation = useCreateGenreMutation();
  const apiGenreRows = useMemo(
    () => (Array.isArray(genresData?.data) ? (genresData?.data ?? []) : []),
    [genresData],
  );
  const apiGenres = useMemo(() => apiGenreRows.map((genre) => genre.name), [apiGenreRows]);
  const apiGenreSlugMap = useMemo(() => buildGenreSlugMap(apiGenreRows), [apiGenreRows]);
  const customGenreSlugMap = useRef<Map<string, string>>(new Map());
  const availableGenres = useMemo(
    () => [...customGenres.filter((g) => !apiGenres.includes(g)), ...apiGenres],
    [customGenres, apiGenres],
  );
  const filteredGenres = useMemo(() => {
    const search = genreInput.toLowerCase().trim();
    return [...availableGenres]
      .sort((a, b) => a.localeCompare(b))
      .filter((g) => g.toLowerCase().includes(search));
  }, [availableGenres, genreInput]);
  const availableUserTypeOptions = userTypesData.map((type) => ({
    value: type.name,
    label: type.name.replace(/_/g, ' '),
  }));

  // File uploads
  const [profileFile, setProfileFile] = useState<File | null>(null);
  const [coverFile, setCoverFile] = useState<File | null>(null);
  const [profilePreview, setProfilePreview] = useState<string | null>(null);
  const [coverPreview, setCoverPreview] = useState<string | null>(null);
  const [coverCropSourceFile, setCoverCropSourceFile] = useState<File | null>(null);
  const [coverCropSourceUrl, setCoverCropSourceUrl] = useState<string | null>(null);
  const [isCoverCropOpen, setIsCoverCropOpen] = useState(false);
  const [isApplyingCoverCrop, setIsApplyingCoverCrop] = useState(false);

  const [profileCropSourceFile, setProfileCropSourceFile] = useState<File | null>(null);
  const [profileCropSourceUrl, setProfileCropSourceUrl] = useState<string | null>(null);
  const [isProfileCropOpen, setIsProfileCropOpen] = useState(false);
  const [isApplyingProfileCrop, setIsApplyingProfileCrop] = useState(false);

  // Deletion flags for backend
  const [isProfileRemoved, setIsProfileRemoved] = useState(false);
  const [isCoverRemoved, setIsCoverRemoved] = useState(false);

  const revokeObjectUrl = useCallback((url: string | null) => {
    if (url?.startsWith('blob:')) {
      URL.revokeObjectURL(url);
    }
  }, []);

  const objectUrlsRef = useRef({
    profilePreview,
    coverPreview,
    coverCropSourceUrl,
    profileCropSourceUrl,
  });

  useEffect(() => {
    objectUrlsRef.current = {
      profilePreview,
      coverPreview,
      coverCropSourceUrl,
      profileCropSourceUrl,
    };
  }, [profilePreview, coverPreview, coverCropSourceUrl, profileCropSourceUrl]);

  useEffect(() => {
    return () => {
      revokeObjectUrl(objectUrlsRef.current.profilePreview);
      revokeObjectUrl(objectUrlsRef.current.coverPreview);
      revokeObjectUrl(objectUrlsRef.current.coverCropSourceUrl);
      revokeObjectUrl(objectUrlsRef.current.profileCropSourceUrl);
    };
  }, [revokeObjectUrl]);

  const snapshotValue = (value: string | null | undefined) => (value ?? '').trim();
  const sortedStringArray = (arr: string[]) => [...arr].map((item) => item.trim()).sort();

  const initialProfileSnapshot = useMemo(
    () => ({
      fullName: snapshotValue(user?.fullName),
      username: snapshotValue(user?.username),
      bio: user?.bio ?? '',
      dob: snapshotValue(user?.dateOfBirth),
      phone: snapshotValue(user?.phoneNumber),
      city: snapshotValue(user?.city),
      state: snapshotValue(user?.state),
      country: snapshotValue(user?.country),
      spotify: snapshotValue(user?.spotify),
      appleMusic: snapshotValue(user?.appleMusic),
      soundcloud: snapshotValue(user?.soundcloud),
      amazonMusic: snapshotValue(user?.amazonMusic),
      youtubeMusic: snapshotValue(user?.youtubeMusic),
      instagram: snapshotValue(user?.instagram),
      twitter: snapshotValue(user?.twitter),
      tiktok: snapshotValue(user?.tiktok),
      website: snapshotValue(user?.website),
      userTypesSelected: sortedStringArray(getUserTypesFromUser(user)),
      genres: sortedStringArray(user?.genres || []),
      hasProfileFile: false,
      hasCoverFile: false,
      isProfileRemoved: false,
      isCoverRemoved: false,
    }),
    [user],
  );

  const currentProfileSnapshot = useMemo(
    () => ({
      fullName: fullName.trim(),
      username: username.trim(),
      bio,
      dob: dob.trim(),
      phone: phone.trim(),
      city: city.trim(),
      state: state.trim(),
      country: country.trim(),
      spotify: spotify.trim(),
      appleMusic: appleMusic.trim(),
      soundcloud: soundcloud.trim(),
      amazonMusic: amazonMusic.trim(),
      youtubeMusic: youtubeMusic.trim(),
      instagram: instagram.trim(),
      twitter: twitter.trim(),
      tiktok: tiktok.trim(),
      website: website.trim(),
      userTypesSelected: sortedStringArray(userTypesSelected),
      genres: sortedStringArray(genres),
      hasProfileFile: Boolean(profileFile),
      hasCoverFile: Boolean(coverFile),
      isProfileRemoved,
      isCoverRemoved,
    }),
    [
      fullName,
      username,
      bio,
      dob,
      phone,
      city,
      state,
      country,
      spotify,
      appleMusic,
      soundcloud,
      amazonMusic,
      youtubeMusic,
      instagram,
      twitter,
      tiktok,
      website,
      userTypesSelected,
      genres,
      profileFile,
      coverFile,
      isProfileRemoved,
      isCoverRemoved,
    ],
  );

  const isDirty = JSON.stringify(currentProfileSnapshot) !== JSON.stringify(initialProfileSnapshot);
  const { isConfirmOpen, requestExit, confirmExit, cancelExit, allowNavigation } =
    useUnsavedChangesGuard({
      isDirty,
    });

  // URL Validation states
  const isValidUrl = (url: string) => {
    if (!url) return true;
    try {
      const parsed = new URL(url.startsWith('http') ? url : `https://${url}`);
      return parsed.hostname.includes('.');
    } catch {
      return false;
    }
  };

  // ── Username check logic ───────────────────────────────────────────────────
  const [debouncedUsername, setDebouncedUsername] = useState('');
  const [isTypingUsername, setIsTypingUsername] = useState(false);

  useEffect(() => {
    if (username === user?.username) {
      setDebouncedUsername('');
      setIsTypingUsername(false);
      return;
    }
    if (username.length < 3) {
      setDebouncedUsername('');
      setIsTypingUsername(false);
      return;
    }

    // Immediate feedback for "brain-friendly" loading
    setIsTypingUsername(true);

    const timer = setTimeout(() => {
      setDebouncedUsername(username.trim());
      setIsTypingUsername(false);
    }, 2000);
    return () => clearTimeout(timer);
  }, [username, user?.username]);

  const { data: availability, isFetching: isCheckingUsername } =
    useUsernameAvailability(debouncedUsername);

  const usernameStatus: 'idle' | 'checking' | 'available' | 'taken' =
    username === user?.username
      ? 'idle'
      : isCheckingUsername || isTypingUsername
        ? 'checking'
        : availability?.available
          ? 'available'
          : availability && !availability.available
            ? 'taken'
            : 'idle';

  const usernameMsg =
    usernameStatus === 'available'
      ? 'Username is available'
      : usernameStatus === 'taken'
        ? availability?.message || 'Username is taken'
        : '';

  // Hydrate from user data
  useEffect(() => {
    if (user) {
      setFullName(user.fullName || '');
      setUsername(user.username || '');
      setBio(user.bio || '');
      setDob(user.dateOfBirth || '');
      setEmail(user.email || '');
      setPhone(user.phoneNumber || '');

      setCity(user.city || '');
      setState(user.state || '');
      setCountry(user.country || '');

      setSpotify(user.spotify || '');
      setAppleMusic(user.appleMusic || '');
      setSoundcloud(user.soundcloud || '');
      setAmazonMusic(user.amazonMusic || '');
      setYoutubeMusic(user.youtubeMusic || '');

      setInstagram(user.instagram || '');
      setTwitter(user.twitter || '');
      setTiktok(user.tiktok || '');
      setWebsite(user.website || '');

      setUserTypesSelected(getUserTypesFromUser(user));
      setGenres(user.genres || []);
    }
  }, [user]);

  // Validation Logic
  const isFormValid = () => {
    if (!fullName.trim()) {
      toast.error('Full Name is required.');
      return false;
    }

    if (!username.trim() || username.trim().length < 3) {
      toast.error('Username must be at least 3 characters long.');
      return false;
    }

    if (genreInput.trim()) {
      toast.error(`Please click 'Add' to include the genre "${genreInput.trim()}" before saving.`);
      return false;
    }

    if (userTypesSelected.length === 0) {
      toast.error('Please select at least one role before saving.');
      return false;
    }

    if (!city.trim() || !state.trim() || !country.trim()) {
      toast.error('Please select your location before saving.');
      return false;
    }

    if (genres.length === 0) {
      toast.error('Please select at least one genre before saving.');
      return false;
    }

    if (bio.length > 500) {
      toast.error('Bio must be less than 500 characters.');
      return false;
    }

    // DOB check (> 13 years)
    if (dob) {
      const birthDate = new Date(dob);
      if (isNaN(birthDate.getTime())) {
        toast.error('Invalid Date of Birth.');
        return false;
      }
      const today = new Date();
      let age = today.getFullYear() - birthDate.getFullYear();
      const monthDiff = today.getMonth() - birthDate.getMonth();
      if (monthDiff < 0 || (monthDiff === 0 && today.getDate() < birthDate.getDate())) {
        age--;
      }
      if (age < 13) {
        toast.error('You must be at least 13 years old.');
        return false;
      }
    }

    // Phone number logic
    if (phone && !/^[+0-9\s-]*$/.test(phone)) {
      toast.error('Phone number contains invalid characters.');
      return false;
    }

    // URL validation check
    const urlFields = [
      { name: 'Spotify', value: spotify },
      { name: 'Apple Music', value: appleMusic },
      { name: 'SoundCloud', value: soundcloud },
      { name: 'Amazon Music', value: amazonMusic },
      { name: 'YouTube Music', value: youtubeMusic },
      { name: 'Instagram', value: instagram },
      { name: 'Twitter', value: twitter },
      { name: 'TikTok', value: tiktok },
      { name: 'Website', value: website },
    ];

    for (const field of urlFields) {
      if (field.value && !isValidUrl(field.value)) {
        toast.error(`Invalid URL for ${field.name}`);
        return false;
      }
    }

    return true;
  };

  const handleSave = async () => {
    if (!isFormValid()) return;

    setIsUploadingImages(true);

    try {
      let finalProfileUrl: string | null | undefined = user?.profPics;
      let finalCoverUrl: string | null | undefined = user?.coverPhoto;

      // 1. Upload Profile Image if new file exists
      if (profileFile) {
        try {
          const [result] = await uploadFilesToCloudinary([profileFile], {
            folder: 'profile_images',
            uploadPreset: 'profile_preset',
          });
          finalProfileUrl = result.secure_url;
        } catch (uploadErr) {
          console.error('Profile image upload failed:', uploadErr);
          toast.error('Failed to upload profile image.');
          setIsUploadingImages(false);
          return;
        }
      } else if (isProfileRemoved) {
        finalProfileUrl = null;
      }

      // 2. Upload Cover Image if new file exists
      if (coverFile) {
        try {
          const [result] = await uploadFilesToCloudinary([coverFile], {
            folder: 'profile_images',
            uploadPreset: 'profile_preset',
          });
          finalCoverUrl = result.secure_url;
        } catch (uploadErr) {
          console.error('Cover photo upload failed:', uploadErr);
          toast.error('Failed to upload cover photo.');
          setIsUploadingImages(false);
          return;
        }
      } else if (isCoverRemoved) {
        finalCoverUrl = null;
      }

      const selectedGenreNames = Array.from(
        new Set(genres.map((genre) => genre.trim()).filter(Boolean)),
      );
      const selectedGenreSlugs = Array.from(
        new Set(
          selectedGenreNames
            .map((genre) => {
              const key = normalizeGenreKey(genre);
              return (
                apiGenreSlugMap.get(key) ??
                customGenreSlugMap.current.get(key) ??
                toSlugCandidate(genre)
              );
            })
            .filter(Boolean),
        ),
      );

      const selectedUserTypesPayload = Array.from(
        new Set(userTypesSelected.map((type) => type.trim()).filter(Boolean)),
      );

      // Build a diff — only send fields that have actually changed
      const snap = initialProfileSnapshot;
      const payload: EditProfilePayload = {};

      if (fullName.trim() !== snap.fullName) payload.full_name = fullName.trim();
      if (username.trim() !== snap.username) payload.username = username.trim();
      if (bio !== snap.bio) payload.bio = bio.trim();
      if (dob.trim() !== snap.dob) payload.date_of_birth = dob.trim();
      if (phone.trim() !== snap.phone) payload.phone_number = phone.trim();
      if (city.trim() !== snap.city) payload.city = city.trim();
      if (state.trim() !== snap.state) payload.state = state.trim();
      if (country.trim() !== snap.country) payload.country = country.trim();
      if (spotify.trim() !== snap.spotify) payload.spotify = spotify.trim();
      if (appleMusic.trim() !== snap.appleMusic) payload.apple_music = appleMusic.trim();
      if (soundcloud.trim() !== snap.soundcloud) payload.soundcloud = soundcloud.trim();
      if (amazonMusic.trim() !== snap.amazonMusic) payload.amazon_music = amazonMusic.trim();
      if (youtubeMusic.trim() !== snap.youtubeMusic) payload.youtube_music = youtubeMusic.trim();
      if (instagram.trim() !== snap.instagram) payload.instagram = instagram.trim();
      if (twitter.trim() !== snap.twitter) payload.twitter = twitter.trim();
      if (tiktok.trim() !== snap.tiktok) payload.tiktok = tiktok.trim();
      if (website.trim() !== snap.website) payload.website = website.trim();

      // Always send user_types and genres if they changed
      if (
        JSON.stringify(sortedStringArray(userTypesSelected)) !==
        JSON.stringify(snap.userTypesSelected)
      ) {
        payload.user_types = selectedUserTypesPayload.map((name) => ({ name, tags: [] }));
      }
      if (JSON.stringify(sortedStringArray(genres)) !== JSON.stringify(snap.genres)) {
        payload.genres = selectedGenreSlugs;
      }

      if (profileFile) {
        payload.prof_pics = finalProfileUrl ?? undefined;
      } else if (isProfileRemoved) {
        payload.prof_pics = null;
        payload.remove_prof_pics = true;
      }

      if (coverFile) {
        payload.cover_photo = finalCoverUrl ?? undefined;
      } else if (isCoverRemoved) {
        payload.cover_photo = null;
        payload.remove_cover_photo = true;
      }

      console.log('[EditProfilePage] Submitting edit profile JSON payload:', payload);

      const data = await editProfile(payload);
      const payloadData =
        (data && typeof data === 'object' && 'data' in data
          ? (data as { data?: unknown }).data
          : data) ?? data;
      const updated =
        payloadData && typeof payloadData === 'object'
          ? (payloadData as Record<string, unknown>)
          : null;
      const updatedUserTypes = updated ? getUserTypesFromUser(updated) : [];

      updateUser({
        fullName: asOptionalString(updated?.full_name) ?? fullName,
        username: asOptionalString(updated?.username) ?? username,
        bio: asOptionalString(updated?.bio) ?? bio,
        dateOfBirth: asOptionalString(updated?.date_of_birth) ?? dob,
        phoneNumber: asOptionalString(updated?.phone_number) ?? phone,
        city: asOptionalString(updated?.city) ?? city,
        state: asOptionalString(updated?.state) ?? state,
        country: asOptionalString(updated?.country) ?? country,
        spotify: asOptionalNullableString(updated?.spotify) ?? (spotify || null),
        appleMusic: asOptionalNullableString(updated?.apple_music) ?? (appleMusic || null),
        soundcloud: asOptionalNullableString(updated?.soundcloud) ?? (soundcloud || null),
        amazonMusic: asOptionalNullableString(updated?.amazon_music) ?? (amazonMusic || null),
        youtubeMusic: asOptionalNullableString(updated?.youtube_music) ?? (youtubeMusic || null),
        instagram: asOptionalNullableString(updated?.instagram) ?? (instagram || null),
        twitter: asOptionalNullableString(updated?.twitter) ?? (twitter || null),
        tiktok: asOptionalNullableString(updated?.tiktok) ?? (tiktok || null),
        website: asOptionalNullableString(updated?.website) ?? (website || null),
        profPics:
          asOptionalString(updated?.profile_image) ||
          asOptionalString(updated?.prof_pics) ||
          asOptionalString(updated?.prof_pics_url) ||
          asOptionalString(updated?.profPics) ||
          (finalProfileUrl ?? undefined),
        coverPhoto:
          asOptionalNullableString(updated?.cover_photo_url) ||
          asOptionalNullableString(updated?.cover_photo) ||
          asOptionalNullableString(updated?.coverPhoto) ||
          finalCoverUrl ||
          null,
        userType: updatedUserTypes[0] || selectedUserTypesPayload[0] || undefined,
        userTypes: updatedUserTypes.length ? updatedUserTypes : selectedUserTypesPayload,
        genres: Array.isArray(updated?.genres) ? (updated.genres as string[]) : selectedGenreNames,
        ...(updated &&
        (updated.profile_incomplete !== undefined || updated.profileIncomplete !== undefined)
          ? {
              profileIncomplete:
                updated.profile_incomplete !== undefined
                  ? Boolean(updated.profile_incomplete)
                  : Boolean(updated.profileIncomplete),
            }
          : {}),
      });

      // Reset image file state so dirty tracking is clean after save
      setProfileFile(null);
      setProfilePreview(null);
      setCoverFile(null);
      setCoverPreview(null);
      setIsProfileRemoved(false);
      setIsCoverRemoved(false);

      allowNavigation();
    } catch (err) {
      console.error('[EditProfilePage] handleSave Error:', err);
    } finally {
      setIsUploadingImages(false);
    }
  };

  const handleExitEditMode = useCallback(() => {
    requestExit(() => navigate({ to: '/profile' }));
  }, [navigate, requestExit]);

  const handlePhoneChange = (val: string) => {
    if (/^[+0-9\s-]*$/.test(val)) {
      setPhone(val);
    }
  };

  const closeCoverCropModal = useCallback(() => {
    setIsCoverCropOpen(false);
    setCoverCropSourceFile(null);
    setCoverCropSourceUrl((prev) => {
      revokeObjectUrl(prev);
      return null;
    });
  }, [revokeObjectUrl]);

  const closeProfileCropModal = useCallback(() => {
    setIsProfileCropOpen(false);
    setProfileCropSourceFile(null);
    setProfileCropSourceUrl((prev) => {
      revokeObjectUrl(prev);
      return null;
    });
  }, [revokeObjectUrl]);

  const applyProfileCrop = useCallback(
    async (cropAreaPixels: Area) => {
      if (!profileCropSourceFile || !profileCropSourceUrl) return;

      setIsApplyingProfileCrop(true);
      try {
        const outputType = profileCropSourceFile.type.startsWith('image/')
          ? profileCropSourceFile.type
          : 'image/jpeg';
        const imageDimensions = await getImageDimensions(profileCropSourceUrl);
        const nextProfileFile = isWholeImageCrop(cropAreaPixels, imageDimensions)
          ? profileCropSourceFile
          : await createCroppedImageFile({
              imageSrc: profileCropSourceUrl,
              cropAreaPixels,
              fileName: profileCropSourceFile.name,
              outputType,
            });
        const nextPreview = URL.createObjectURL(nextProfileFile);

        revokeObjectUrl(profilePreview);
        setProfileFile(nextProfileFile);
        setProfilePreview(nextPreview);
        setIsProfileRemoved(false);
        closeProfileCropModal();
      } catch (error) {
        console.error('Profile crop failed:', error);
        toast.error('Failed to crop profile photo. Please try another image.');
      } finally {
        setIsApplyingProfileCrop(false);
      }
    },
    [
      closeProfileCropModal,
      profileCropSourceFile,
      profileCropSourceUrl,
      profilePreview,
      revokeObjectUrl,
    ],
  );

  const applyCoverCrop = useCallback(
    async (cropAreaPixels: Area) => {
      if (!coverCropSourceFile || !coverCropSourceUrl) return;

      setIsApplyingCoverCrop(true);
      try {
        const outputType = coverCropSourceFile.type.startsWith('image/')
          ? coverCropSourceFile.type
          : 'image/jpeg';
        const imageDimensions = await getImageDimensions(coverCropSourceUrl);
        const nextCoverFile = isWholeImageCrop(cropAreaPixels, imageDimensions)
          ? coverCropSourceFile
          : await createCroppedImageFile({
              imageSrc: coverCropSourceUrl,
              cropAreaPixels,
              fileName: coverCropSourceFile.name,
              outputType,
            });
        const nextPreview = URL.createObjectURL(nextCoverFile);

        revokeObjectUrl(coverPreview);
        setCoverFile(nextCoverFile);
        setCoverPreview(nextPreview);
        setIsCoverRemoved(false);
        closeCoverCropModal();
      } catch (error) {
        console.error('Cover crop failed:', error);
        toast.error('Failed to crop cover photo. Please try another image.');
      } finally {
        setIsApplyingCoverCrop(false);
      }
    },
    [closeCoverCropModal, coverCropSourceFile, coverCropSourceUrl, coverPreview, revokeObjectUrl],
  );

  const handleFileChange = async (
    e: React.ChangeEvent<HTMLInputElement>,
    type: 'profile' | 'cover',
  ) => {
    const file = e.target.files?.[0];
    if (!file) return;

    if (!file.type.startsWith('image/')) {
      toast.error('Only image files are allowed.');
      return;
    }
    if (file.size > 5 * 1024 * 1024) {
      toast.error('File size must be less than 5MB.');
      return;
    }

    // Convert HEIC to JPEG for browser compatibility before cropping
    const convertedFile = await convertHeicToJpeg(file);

    if (type === 'profile') {
      const sourceUrl = URL.createObjectURL(convertedFile);
      setProfileCropSourceFile(convertedFile);
      setProfileCropSourceUrl((prev) => {
        revokeObjectUrl(prev);
        return sourceUrl;
      });
      setIsProfileCropOpen(true);
    } else {
      const sourceUrl = URL.createObjectURL(convertedFile);
      setCoverCropSourceFile(convertedFile);
      setCoverCropSourceUrl((prev) => {
        revokeObjectUrl(prev);
        return sourceUrl;
      });
      setIsCoverCropOpen(true);
    }
  };

  const handleLocationSelected = (location: LocationBody | null) => {
    console.log('[EditProfilePage] handleLocationSelected called:', location);
    if (location) {
      setCity(location.city || '');
      setState(location.state || '');
      setCountry(location.country || '');
      console.log(
        '[EditProfilePage] Location set - city:',
        location.city,
        'state:',
        location.state,
        'country:',
        location.country,
      );
    } else {
      setCity('');
      setState('');
      setCountry('');
      console.log('[EditProfilePage] Location cleared');
    }
  };

  // Build LocationBody for initial value
  const initialLocation: LocationBody | null = user?.city
    ? {
        address: [user.city, user.state, user.country].filter(Boolean).join(', '),
        city: user.city,
        state: user.state || '',
        country: user.country || '',
        latitude: 0,
        longitude: 0,
      }
    : null;

  const handleCreateGenre = async () => {
    const name = genreInput.trim();
    if (!name) return;
    if (availableGenres.some((g) => g.toLowerCase() === name.toLowerCase())) {
      toast.error('Genre already exists.');
      return;
    }

    try {
      const created = await createGenreMutation.mutateAsync(name);
      const createdName = created?.name || name;
      const createdSlug =
        typeof created?.slug === 'string' && created.slug.trim()
          ? created.slug.trim()
          : createdName;
      const normalizedName = normalizeGenreKey(createdName);
      const normalizedSlug = normalizeGenreKey(createdSlug);
      customGenreSlugMap.current.set(normalizedName, normalizedSlug);
      customGenreSlugMap.current.set(normalizedSlug, normalizedSlug);
      setCustomGenres((prev) => (prev.includes(createdName) ? prev : [...prev, createdName]));
      setGenres((prev) => (prev.includes(createdName) ? prev : [...prev, createdName]));
      setGenreInput('');
      await queryClient.invalidateQueries({ queryKey: ['genres'] });
      toast.success('Genre added.');
    } catch (error) {
      console.error('Create genre failed:', error);
    }
  };

  const toggleUserType = (name: string) => {
    setUserTypesSelected((prev) =>
      prev.includes(name) ? prev.filter((type) => type !== name) : [...prev, name],
    );
  };

  return (
    <div className="min-h-screen bg-black text-white pb-32">
      <Header />

      {/* Spacer to push content below the fixed global header without affecting sticky behavior */}
      <div className="h-[var(--header-height)]" />

      {/* Sticky Sub-Header */}
      <div
        className={[
          'sticky left-0 right-0 z-40',
          'top-[var(--header-height)]',
          // Visual polish: blend with global header + readable separation from content
          'bg-gradient-to-b from-black/95 via-black/80 to-black/65',
          'backdrop-blur-2xl',
          'border-b border-white/10',
          'shadow-[0_14px_40px_rgba(0,0,0,0.55)]',
          'h-18 md:h-20 flex items-center transition-all duration-300',
        ].join(' ')}
      >
        <div className="max-w-[1440px] w-full mx-auto px-4 sm:px-6 lg:px-8 flex items-center justify-between gap-4">
          <div className="flex flex-col gap-0.5">
            <nav className="flex items-center gap-2 px-1 text-zinc-500 text-[12px] font-medium tracking-wide">
              <Link
                to="/profile"
                onClick={(e) => {
                  e.preventDefault();
                  handleExitEditMode();
                }}
                className="flex items-center gap-1.5 hover:text-white transition-all group/crumb"
              >
                <FiUser
                  size={13}
                  className="text-blue-500/80 group-hover/crumb:text-blue-400 transition-colors"
                />
                <span>Profile</span>
              </Link>
              <span className="text-zinc-800 font-light translate-y-[0.5px]">/</span>
              <span className="text-zinc-200">Edit Profile</span>
            </nav>
            <h1 className="text-white font-bold text-[20px] md:text-[26px] tracking-tight">
              Edit Profile
            </h1>
          </div>

          <div className="flex items-center gap-3 shrink-0">
            <button
              onClick={handleExitEditMode}
              className="hidden sm:flex items-center gap-2 px-5 md:px-6 py-2 md:py-2.5 rounded-full bg-zinc-900 border border-white/10 text-white text-[13px] md:text-[14px] font-bold hover:bg-zinc-800 transition-all shadow-lg active:scale-95"
            >
              Cancel
            </button>
            <button
              onClick={handleSave}
              disabled={
                isPending ||
                !fullName.trim() ||
                !username.trim() ||
                username.trim().length < 3 ||
                usernameStatus === 'taken' ||
                usernameStatus === 'checking' ||
                genreInput.trim().length > 0 ||
                userTypesSelected.length === 0 ||
                !city.trim() ||
                !state.trim() ||
                !country.trim() ||
                genres.length === 0
              }
              className="flex items-center gap-2 px-5 md:px-6 py-2 md:py-2.5 rounded-full bg-white text-black text-[13px] md:text-[14px] font-bold hover:bg-zinc-200 transition-all shadow-lg active:scale-95 disabled:opacity-40 disabled:cursor-not-allowed"
            >
              <FiSave size={16} />
              {isPending ? 'Saving...' : 'Save Changes'}
            </button>
          </div>
        </div>
      </div>

      <main className="pt-10 max-w-[1440px] mx-auto px-4 sm:px-6 lg:px-8">
        {/* Missing required fields alert */}
        {user?.profileIncomplete && (
          <div className="mb-8 bg-amber-500/5 border border-amber-500/20 rounded-2xl p-4 sm:p-5">
            <div className="flex items-start gap-3">
              <div className="w-8 h-8 rounded-lg bg-amber-500/10 border border-amber-500/20 flex items-center justify-center shrink-0 mt-0.5">
                <FiAlertCircle size={16} className="text-amber-400" />
              </div>
              <div className="flex-1">
                <p className="text-white font-semibold text-[14px] mb-1">
                  Complete these required fields
                </p>
                <p className="text-zinc-500 text-[13px] mb-3">
                  Your profile is incomplete. Please fill in the missing information below to unlock
                  all features.
                </p>
                <div className="flex flex-wrap gap-2">
                  {userTypesSelected.length === 0 && (
                    <span className="px-2.5 py-1 rounded-lg bg-zinc-900/60 border border-white/8 text-zinc-400 text-[12px] font-medium">
                      Add at least one role
                    </span>
                  )}
                  {(!city.trim() || !state.trim() || !country.trim()) && (
                    <span className="px-2.5 py-1 rounded-lg bg-zinc-900/60 border border-white/8 text-zinc-400 text-[12px] font-medium">
                      Set your location
                    </span>
                  )}
                  {genres.length === 0 && (
                    <span className="px-2.5 py-1 rounded-lg bg-zinc-900/60 border border-white/8 text-zinc-400 text-[12px] font-medium">
                      Select at least one genre
                    </span>
                  )}
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Cover Photo */}
        <div className="relative w-full aspect-[3/1] rounded-2xl overflow-hidden bg-zinc-900 border border-white/8 mb-10 group">
          <CoverImage
            src={coverPreview || (user?.coverPhoto && !isCoverRemoved ? user?.coverPhoto : null)}
            alt="Cover"
          />

          <div className="absolute top-4 left-4 flex flex-col gap-0.5">
            <span className="text-white font-semibold text-[15px] opacity-70">Cover Photo</span>
            <span className="text-zinc-500 text-[11px]">3:1 aspect ratio</span>
          </div>

          <div className="absolute bottom-4 right-4 flex items-center gap-2 md:opacity-0 md:group-hover:opacity-100 transition-opacity opacity-100">
            <label className="flex items-center gap-2 px-4 py-2 rounded-full bg-white text-black text-[12px] font-bold cursor-pointer shadow-xl hover:bg-zinc-100 transition-colors">
              <FiCamera size={14} />
              Change Photo
              <input
                type="file"
                accept="image/*"
                className="hidden"
                onChange={(e) => handleFileChange(e, 'cover')}
                onClick={(e) => {
                  (e.target as HTMLInputElement).value = '';
                }}
              />
            </label>
            <button
              onClick={(e) => {
                e.stopPropagation();
                setCoverFile(null);
                revokeObjectUrl(coverPreview);
                setCoverPreview(null);
                setIsCoverRemoved(true);
              }}
              className="flex items-center gap-2 px-4 py-2 rounded-full bg-black/40 backdrop-blur-md border border-red-500/30 text-red-400 text-[12px] font-bold hover:bg-red-500/10 transition-colors"
            >
              <FiTrash2 size={14} />
              Remove Photo
            </button>
          </div>
        </div>

        {/* Profile Info Header (Avatar + Buttons) */}
        <div className="flex flex-col md:flex-row md:items-center gap-6 mb-12">
          <div className="relative w-[128px] h-[128px] rounded-full bg-zinc-800 border-2 border-white/10 overflow-hidden shrink-0 group">
            <Avatar
              src={profilePreview || (user?.profPics && !isProfileRemoved ? user?.profPics : null)}
              alt="avatar"
              name={user?.fullName || user?.username}
              size="4xl"
              className="w-full h-full"
              iconSize={52}
            />
            <label className="absolute inset-0 bg-black/50 md:opacity-0 md:group-hover:opacity-100 opacity-100 flex items-center justify-center transition-opacity cursor-pointer">
              <FiCamera size={24} className="text-white" />
              <input
                type="file"
                accept="image/*"
                className="hidden"
                onChange={(e) => handleFileChange(e, 'profile')}
                onClick={(e) => {
                  (e.target as HTMLInputElement).value = '';
                }}
              />
            </label>
          </div>

          <div className="flex flex-col gap-2">
            <p className="text-zinc-600 text-[11px]">Recommended: 1:1 square</p>
            <div className="flex gap-2">
              <label className="flex items-center gap-2 px-5 py-2.5 rounded-full bg-white text-black text-[13px] font-bold cursor-pointer hover:bg-zinc-100 transition-all shadow-lg active:scale-95">
                <FiCamera size={15} />
                Change Photo
                <input
                  type="file"
                  accept="image/*"
                  className="hidden"
                  onChange={(e) => handleFileChange(e, 'profile')}
                  onClick={(e) => {
                    (e.target as HTMLInputElement).value = '';
                  }}
                />
              </label>
              <button
                onClick={() => {
                  setProfileFile(null);
                  setProfilePreview(null);
                  setIsProfileRemoved(true);
                }}
                className="flex items-center gap-2 px-5 py-2.5 rounded-full bg-black/40 border border-red-500/30 text-red-400 text-[13px] font-bold hover:bg-red-500/10 transition-all shadow-lg active:scale-95"
              >
                <FiTrash2 size={15} />
                Remove Photo
              </button>
            </div>
          </div>
        </div>

        {/* Form Content */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-x-12 gap-y-10">
          {/* Column 1 (General) */}
          <div className="flex flex-col gap-10">
            <FormSection title="General">
              <FormInput
                id="full-name"
                label="Full Name"
                icon={FiUser}
                iconColor="#52525b"
                placeholder="Ava Brown"
                value={fullName}
                onChange={setFullName}
              />
              <FormInput
                id="username"
                label="Username"
                icon={FiAtSign}
                iconColor="#52525b"
                placeholder="asheera"
                value={username}
                onChange={setUsername}
                status={usernameStatus}
                statusMsg={usernameMsg}
              />
              <div className="flex flex-col gap-3">
                <div className="flex items-center justify-between px-1">
                  <label className="text-zinc-400 text-[13px] font-medium">Your Roles *</label>
                  <span className="text-[11px] text-zinc-500 font-medium">
                    {userTypesSelected.length} selected
                  </span>
                </div>
                <div className="rounded-2xl border border-white/8 bg-zinc-900/40 p-3">
                  <div className="flex flex-wrap gap-2.5">
                    {availableUserTypeOptions.map((option) => {
                      const isSelected = userTypesSelected.includes(option.value);
                      return (
                        <button
                          key={option.value}
                          type="button"
                          onClick={() => toggleUserType(option.value)}
                          className={`px-3.5 py-2 rounded-full text-[12px] font-semibold transition-all border whitespace-nowrap ${
                            isSelected
                              ? 'bg-blue-600 text-white border-blue-500 shadow-[0_0_15px_rgba(59,130,246,0.35)]'
                              : 'bg-zinc-800/70 text-zinc-300 border-white/10 hover:border-white/25 hover:bg-zinc-800'
                          }`}
                        >
                          {option.label}
                        </button>
                      );
                    })}
                  </div>
                </div>
              </div>
            </FormSection>

            <FormSection title="Musical Styles">
              <div className="flex flex-col gap-3">
                <div className="flex items-center justify-between px-1">
                  <label className="text-zinc-400 text-[13px] font-medium">Select Genres *</label>
                  <span className="text-[11px] text-zinc-500 font-medium">
                    {genres.length} selected
                  </span>
                </div>

                <div className="rounded-2xl border border-white/8 bg-zinc-900/40 p-3">
                  {/* Search Input */}
                  <div className="relative mb-3">
                    <FiSearch
                      size={14}
                      className="absolute left-3.5 top-1/2 -translate-y-1/2 text-zinc-500 pointer-events-none"
                    />
                    <input
                      type="text"
                      value={genreInput}
                      onChange={(e) => setGenreInput(e.target.value)}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter' && filteredGenres.length === 0 && genreInput.trim()) {
                          handleCreateGenre();
                        }
                      }}
                      placeholder="Search or create a genre..."
                      className="w-full bg-zinc-950/60 border border-white/10 rounded-xl pl-10 pr-10 py-2.5 text-[13px] text-white placeholder:text-zinc-600 focus:outline-none focus:ring-0 focus:border-blue-500/50 transition-colors"
                    />
                    {genreInput && (
                      <button
                        type="button"
                        onClick={() => setGenreInput('')}
                        className="absolute right-3.5 top-1/2 -translate-y-1/2 text-zinc-500 hover:text-zinc-300 transition-colors"
                      >
                        <FiX size={14} />
                      </button>
                    )}
                  </div>

                  {isLoadingGenres ? (
                    <p className="text-zinc-500 text-[13px] px-1 py-2">Loading genres...</p>
                  ) : filteredGenres.length === 0 ? (
                    <div className="flex flex-col items-center gap-3 py-6 px-1">
                      <p className="text-zinc-500 text-[13px] text-center">
                        {genreInput ? `No genres matching "${genreInput}"` : 'No genres available.'}
                      </p>
                      {genreInput.trim() && (
                        <button
                          type="button"
                          onClick={handleCreateGenre}
                          disabled={createGenreMutation.isPending}
                          className="flex items-center gap-1.5 px-4 py-2 rounded-xl bg-blue-600/20 border border-blue-500/30 hover:bg-blue-600/30 text-blue-400 text-[12px] font-semibold transition-all"
                        >
                          {createGenreMutation.isPending ? (
                            <div className="w-3 h-3 border-2 border-blue-400/20 border-t-blue-400 rounded-full animate-spin" />
                          ) : (
                            <FiPlus size={14} />
                          )}
                          Add "{genreInput.trim()}"
                        </button>
                      )}
                    </div>
                  ) : (
                    <div className="flex flex-wrap gap-2.5 max-h-[220px] overflow-y-auto pr-1">
                      {filteredGenres.map((genre) => {
                        const isSelected = genres.includes(genre);
                        return (
                          <button
                            key={genre}
                            type="button"
                            onClick={() => {
                              if (isSelected) {
                                setGenres(genres.filter((g) => g !== genre));
                              } else {
                                setGenres([...genres, genre]);
                              }
                            }}
                            className={`px-3.5 py-2 rounded-full text-[12px] font-semibold transition-all border whitespace-nowrap ${
                              isSelected
                                ? 'bg-blue-600 text-white border-blue-500 shadow-[0_0_15px_rgba(59,130,246,0.35)]'
                                : 'bg-zinc-800/70 text-zinc-300 border-white/10 hover:border-white/25 hover:bg-zinc-800'
                            }`}
                          >
                            {genre}
                          </button>
                        );
                      })}
                    </div>
                  )}
                </div>
              </div>
            </FormSection>

            <FormSection title="Contact Details">
              <div className="flex flex-col gap-1.5">
                <label className="text-zinc-400 text-[13px] font-medium flex items-center gap-2">
                  <FiMapPin size={14} className="text-blue-500" />
                  Place of Origin / Current Location *
                </label>
                <LocationSearchInput
                  onLocationSelected={handleLocationSelected}
                  initialLocation={initialLocation}
                />
              </div>

              {/* Higher Fidelity Map Preview */}
              <div className="w-full h-[200px] rounded-2xl overflow-hidden bg-zinc-900 border border-white/12 relative">
                <img
                  src="https://images.unsplash.com/photo-1526778548025-fa2f459cd5c1?auto=format&fit=crop&q=80&w=800"
                  className="w-full h-full object-cover opacity-20 grayscale brightness-50 contrast-125"
                  alt="Map background"
                />
                <div className="absolute inset-0 bg-linear-to-t from-black via-transparent to-transparent" />
                <div className="absolute inset-0 flex items-center justify-center">
                  <div className="flex flex-col items-center gap-2 bg-black/60 backdrop-blur-md p-4 px-6 rounded-2xl border border-white/10 shadow-2xl scale-95 md:scale-100">
                    <div className="w-10 h-10 bg-blue-500/20 rounded-full flex items-center justify-center">
                      <FiMapPin size={20} className="text-blue-500" />
                    </div>
                    <span className="text-white font-medium text-[13px] tracking-wide uppercase">
                      {city ? `${city}, ${state}` : 'Location Preview'}
                    </span>
                  </div>
                </div>
              </div>
            </FormSection>
          </div>

          {/* Column 2 (Personal Details) */}
          <div className="flex flex-col gap-10">
            <FormSection title="Personal Details">
              <div className="flex flex-col gap-1.5">
                <label className="text-zinc-400 text-[13px] font-medium ml-1">Date of Birth</label>
                <DateInput
                  name="dob"
                  value={dob}
                  onChange={(value) => setDob(value)}
                  icon={FiCalendar}
                  className="h-12 bg-zinc-900/60 border-white/8 focus:border-blue-500/50"
                />
              </div>
              <div className="flex flex-col gap-1.5 w-full">
                <label
                  htmlFor="bio"
                  className="text-zinc-400 text-[13px] font-medium flex justify-between items-center"
                >
                  <span className="inline-flex items-center gap-2">
                    <span>Bio</span>
                    <span className="inline-flex items-center rounded-full border border-zinc-700 bg-zinc-900 px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wide text-zinc-400">
                      Optional
                    </span>
                  </span>
                  <span className={bio.length > 500 ? 'text-red-400' : 'text-zinc-600'}>
                    {bio.length}/500
                  </span>
                </label>
                <textarea
                  id="bio"
                  rows={5}
                  placeholder="Add bio..."
                  value={bio}
                  maxLength={505}
                  onChange={(e) => setBio(e.target.value)}
                  className={`px-4 py-3 rounded-xl bg-zinc-900/60 border ${bio.length > 500 ? 'border-red-500/40' : 'border-white/8'} text-white text-[14px] placeholder:text-zinc-600 focus:outline-none focus:ring-0 focus:border-blue-500/50 resize-none transition-colors w-full`}
                />
              </div>
            </FormSection>

            {/* Email and Phone */}
            <div className="flex flex-col gap-6 pt-4">
              <FormInput
                id="email"
                label="Email address"
                icon={FiMail}
                iconColor="#3b82f6"
                type="email"
                value={email}
                onChange={() => {}}
                disabled
              />
              <FormInput
                id="phone"
                label="Phone Number"
                optional
                icon={FiPhone}
                iconColor="#3b82f6"
                type="tel"
                placeholder="Enter phone number"
                value={phone}
                onChange={handlePhoneChange}
              />
            </div>
          </div>

          <div className="md:col-span-2 h-px bg-white/5 my-4" />

          {/* Music Platforms */}
          <div className="md:col-span-2">
            <FormSection title="Music Platforms">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-x-12 gap-y-6">
                <FormInput
                  id="spotify"
                  label="Spotify"
                  optional
                  icon={SiSpotify}
                  iconColor="#1DB954"
                  placeholder="https://spotify.com/artist/..."
                  value={spotify}
                  onChange={setSpotify}
                  status={spotify && !isValidUrl(spotify) ? 'error' : 'idle'}
                  statusMsg={spotify && !isValidUrl(spotify) ? 'Enter a valid URL' : ''}
                />
                <FormInput
                  id="soundcloud"
                  label="SoundCloud"
                  optional
                  icon={SiSoundcloud}
                  iconColor="#FF5500"
                  placeholder="https://soundcloud.com/..."
                  value={soundcloud}
                  onChange={setSoundcloud}
                  status={soundcloud && !isValidUrl(soundcloud) ? 'error' : 'idle'}
                  statusMsg={soundcloud && !isValidUrl(soundcloud) ? 'Enter a valid URL' : ''}
                />
                <FormInput
                  id="apple-music"
                  label="Apple Music"
                  optional
                  icon={SiApplemusic}
                  iconColor="#fc3c44"
                  placeholder="https://music.apple.com/..."
                  value={appleMusic}
                  onChange={setAppleMusic}
                  status={appleMusic && !isValidUrl(appleMusic) ? 'error' : 'idle'}
                  statusMsg={appleMusic && !isValidUrl(appleMusic) ? 'Enter a valid URL' : ''}
                />
                <FormInput
                  id="amazon-music"
                  label="Amazon Music"
                  optional
                  icon={FaAmazon}
                  iconColor="#FF9900"
                  placeholder="https://music.amazon.com/..."
                  value={amazonMusic}
                  onChange={setAmazonMusic}
                  status={amazonMusic && !isValidUrl(amazonMusic) ? 'error' : 'idle'}
                  statusMsg={amazonMusic && !isValidUrl(amazonMusic) ? 'Enter a valid URL' : ''}
                />
                <FormInput
                  id="youtube-music"
                  label="YouTube Music"
                  optional
                  icon={SiYoutube}
                  iconColor="#FF0000"
                  placeholder="https://music.youtube.com/..."
                  value={youtubeMusic}
                  onChange={setYoutubeMusic}
                  status={youtubeMusic && !isValidUrl(youtubeMusic) ? 'error' : 'idle'}
                  statusMsg={youtubeMusic && !isValidUrl(youtubeMusic) ? 'Enter a valid URL' : ''}
                />
              </div>
            </FormSection>
          </div>

          <div className="md:col-span-2 h-px bg-white/5 my-4" />

          {/* Socials */}
          <div className="md:col-span-2">
            <FormSection title="Socials">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-x-12 gap-y-6">
                <FormInput
                  id="instagram"
                  label="Instagram"
                  optional
                  icon={SiInstagram}
                  iconColor="#E1306C"
                  placeholder="https://instagram.com/..."
                  value={instagram}
                  onChange={setInstagram}
                  status={instagram && !isValidUrl(instagram) ? 'error' : 'idle'}
                  statusMsg={instagram && !isValidUrl(instagram) ? 'Enter a valid URL' : ''}
                />
                <FormInput
                  id="twitter"
                  label="Twitter"
                  optional
                  icon={FaXTwitter}
                  iconColor="#fff"
                  placeholder="https://twitter.com/..."
                  value={twitter}
                  onChange={setTwitter}
                  status={twitter && !isValidUrl(twitter) ? 'error' : 'idle'}
                  statusMsg={twitter && !isValidUrl(twitter) ? 'Enter a valid URL' : ''}
                />
                <FormInput
                  id="tiktok"
                  label="TikTok"
                  optional
                  icon={SiTiktok}
                  iconColor="#69C9D0"
                  placeholder="https://tiktok.com/@..."
                  value={tiktok}
                  onChange={setTiktok}
                  status={tiktok && !isValidUrl(tiktok) ? 'error' : 'idle'}
                  statusMsg={tiktok && !isValidUrl(tiktok) ? 'Enter a valid URL' : ''}
                />
                <FormInput
                  id="website"
                  label="Website"
                  optional
                  icon={FiGlobe}
                  iconColor="#3b82f6"
                  placeholder="https://yourwebsite.com"
                  value={website}
                  onChange={setWebsite}
                  status={website && !isValidUrl(website) ? 'error' : 'idle'}
                  statusMsg={website && !isValidUrl(website) ? 'Enter a valid URL' : ''}
                />
              </div>
            </FormSection>
          </div>
        </div>

        <footer className="mt-24 pb-32 pt-8 border-t border-white/12 text-center">
          <p className="text-zinc-600 text-[13px] font-medium tracking-wider uppercase">
            © 2025 Musicade
          </p>
        </footer>
      </main>

      <BottomNav />

      <CoverCropModal
        isOpen={isCoverCropOpen}
        imageSrc={coverCropSourceUrl}
        isSubmitting={isApplyingCoverCrop}
        onCancel={closeCoverCropModal}
        onConfirm={applyCoverCrop}
      />

      <CoverCropModal
        isOpen={isProfileCropOpen}
        imageSrc={profileCropSourceUrl}
        isSubmitting={isApplyingProfileCrop}
        aspect={1 / 1}
        title="Adjust profile photo"
        description="Drag to choose what appears in your profile photo"
        cropShape="round"
        objectFit="contain"
        onCancel={closeProfileCropModal}
        onConfirm={applyProfileCrop}
      />

      {isConfirmOpen && <UnsavedChangesModal onConfirm={confirmExit} onCancel={cancelExit} />}
    </div>
  );
};
